import React, { useEffect, useState } from "react";
import { Button, CircularProgress, Divider, Typography } from "@material-ui/core";
import { toast } from "react-toastify";
import ContactAvatar from "../ContactAvatar";
import { waLinkFor } from "../../utils/parseVcard";
import api from "../../services/api";
import toastError from "../../errors/toastError";

// Pré-visualização de um contato compartilhado (vCard), no mesmo padrão
// visual do LocationPreview: bloco de informação + divisor + ações.
//
// Ao montar, verifica (somente-leitura) se o número já é um contato
// cadastrado na empresa, e ajusta as ações disponíveis:
// - Não cadastrado: "Salvar contato" + "Abrir no WhatsApp"
// - Já cadastrado:  "Iniciar Conversa" + "Agendar Mensagem"
const VcardPreview = ({ name, numbers = [], onStartConversation, onScheduleMessage }) => {
  const primaryNumber = numbers[0];
  const primaryNumberRaw = primaryNumber?.raw;
  const waLink = primaryNumber ? waLinkFor(primaryNumber.raw) : null;

  const [checking, setChecking] = useState(Boolean(primaryNumber));
  const [contact, setContact] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;

    if (!primaryNumberRaw) {
      setChecking(false);
      return undefined;
    }

    setChecking(true);
    api
      .get(`/contacts/by-number/${primaryNumberRaw}`)
      .then(({ data }) => {
        if (active) setContact(data.contact || null);
      })
      .catch(() => {
        // Falha na checagem não deve travar a pré-visualização — o
        // usuário ainda pode tentar "Salvar contato" normalmente.
      })
      .finally(() => {
        if (active) setChecking(false);
      });

    return () => {
      active = false;
    };
  }, [primaryNumberRaw]);

  const handleOpenWhatsapp = () => {
    if (waLink) window.open(waLink, "_blank");
  };

  const handleSaveContact = async () => {
    if (!primaryNumber || saving) return;
    setSaving(true);
    try {
      const { data } = await api.post("/contacts", {
        name,
        number: primaryNumber.raw,
      });
      setContact(data);
      toast.success("Contato salvo com sucesso.");
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleStartConversation = () => {
    if (contact && typeof onStartConversation === "function") {
      onStartConversation(contact);
    }
  };

  const handleScheduleMessage = () => {
    if (contact && typeof onScheduleMessage === "function") {
      onScheduleMessage(contact);
    }
  };

  return (
    <div style={{ minWidth: "250px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "8px 4px",
        }}
      >
        <ContactAvatar name={name} />
        <div style={{ minWidth: 0 }}>
          <Typography variant="subtitle2" style={{ fontWeight: 700 }} noWrap>
            {name}
          </Typography>
          {numbers.length > 0 ? (
            numbers.map((n) => (
              <Typography
                key={n.raw}
                variant="body2"
                color="textSecondary"
                noWrap
              >
                {n.display}
              </Typography>
            ))
          ) : (
            <Typography variant="caption" color="textSecondary">
              Sem telefone
            </Typography>
          )}
        </div>
      </div>
      <Divider />

      {checking ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "10px 0" }}>
          <CircularProgress size={18} />
        </div>
      ) : contact ? (
        <div style={{ display: "flex" }}>
          <Button fullWidth color="primary" onClick={handleStartConversation}>
            Iniciar Conversa
          </Button>
          <Button fullWidth onClick={handleScheduleMessage}>
            Agendar Mensagem
          </Button>
        </div>
      ) : (
        <div style={{ display: "flex" }}>
          <Button
            fullWidth
            color="primary"
            onClick={handleSaveContact}
            disabled={saving || !primaryNumber}
          >
            {saving ? <CircularProgress size={16} /> : "Salvar contato"}
          </Button>
          <Button fullWidth onClick={handleOpenWhatsapp} disabled={!waLink}>
            Abrir no WhatsApp
          </Button>
        </div>
      )}
    </div>
  );
};

export default VcardPreview;
